import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { ConcessionComment } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface CommentsTabProps {
  comments: ConcessionComment[];
  newComment: string;
  isInternalComment: boolean;
  commentLoading: boolean;
  isCustomer: boolean;
  onCommentChange: (value: string) => void;
  onInternalChange: (value: boolean) => void;
  onAddComment: () => void;
}

export function CommentsTab({
  comments,
  newComment,
  isInternalComment,
  commentLoading,
  isCustomer,
  onCommentChange,
  onInternalChange,
  onAddComment,
}: CommentsTabProps) {
  const { formatDateTime } = useFormatters();

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {/* Add Comment Form */}
        <div className="space-y-3">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => onCommentChange(e.target.value)}
            rows={3}
          />
          <div className="flex items-center justify-between">
            {isCustomer && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isInternalComment}
                  onChange={(e) => onInternalChange(e.target.checked)}
                />
                Internal comment (not visible to supplier)
              </label>
            )}
            <Button
              size="sm"
              onClick={onAddComment}
              disabled={commentLoading || !newComment.trim()}
            >
              {commentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Comment'}
            </Button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-4 border-t pt-4">
          {comments.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No comments yet</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className={`p-3 rounded-lg ${comment.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-muted/50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{comment.created_by_name || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    {comment.is_internal && (
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">
                        Internal
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDateTime(comment.created_at)}</span>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
